import type { AgentState, AgentAction, ToolCallDisplay } from '../types/agent.types';

export const initialAgentState: AgentState = {
  panelOpen: false,
  connectionStatus: 'disconnected',
  activeConversationId: null,
  messages: [],
  conversations: [],
  isStreaming: false,
  streamingContent: '',
  streamingToolCalls: [],
  error: null,
  errorCode: null,
  showHistory: false,
};

export function agentReducer(state: AgentState, action: AgentAction): AgentState {
  switch (action.type) {
    case 'TOGGLE_PANEL':
      return { ...state, panelOpen: !state.panelOpen };

    case 'OPEN_PANEL':
      return { ...state, panelOpen: true };

    case 'CLOSE_PANEL':
      return { ...state, panelOpen: false };

    case 'SET_CONNECTION_STATUS':
      return { ...state, connectionStatus: action.payload };

    case 'SET_ACTIVE_CONVERSATION':
      return {
        ...state,
        activeConversationId: action.payload,
        showHistory: false,
      };

    case 'CLEAR_CONVERSATION':
      return {
        ...state,
        activeConversationId: null,
        messages: [],
        streamingContent: '',
        streamingToolCalls: [],
        isStreaming: false,
        error: null,
        errorCode: null,
      };

    case 'ADD_USER_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };

    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };

    case 'START_STREAMING':
      return {
        ...state,
        isStreaming: true,
        streamingContent: '',
        streamingToolCalls: [],
        error: null,
        errorCode: null,
      };

    case 'APPEND_TOKEN':
      return {
        ...state,
        streamingContent: state.streamingContent + action.payload,
      };

    case 'ADD_TOOL_CALL':
      return {
        ...state,
        streamingToolCalls: [...state.streamingToolCalls, action.payload],
      };

    case 'UPDATE_TOOL_CALL': {
      const { toolUseId, status, result } = action.payload;
      return {
        ...state,
        streamingToolCalls: state.streamingToolCalls.map((tc: ToolCallDisplay) =>
          tc.toolUseId === toolUseId ? { ...tc, status, result } : tc
        ),
      };
    }

    case 'FINISH_STREAMING': {
      // Guard: ignore if already finalized (e.g. optimistic cancel + backend cancelled event)
      if (!state.isStreaming) return state;
      const { messageId, tokenUsage, credits } = action.payload;
      const assistantMessage = {
        messageId: messageId || `msg-${Date.now()}`,
        role: 'assistant' as const,
        content: state.streamingContent,
        createdAt: new Date().toISOString(),
        tokenUsage,
        ...(credits !== undefined && { credits }),
        toolCalls: state.streamingToolCalls.length > 0 ? [...state.streamingToolCalls] : undefined,
      };
      return {
        ...state,
        isStreaming: false,
        streamingContent: '',
        streamingToolCalls: [],
        messages: [...state.messages, assistantMessage],
      };
    }

    case 'SET_MESSAGE_FEEDBACK': {
      const { messageId, feedback } = action.payload;
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.messageId !== messageId) return msg;
          if (feedback === undefined) {
            const { feedback: _, ...rest } = msg;
            return rest;
          }
          return { ...msg, feedback };
        }),
      };
    }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        errorCode: action.payload === null ? null : (action.code ?? null),
        isStreaming: false,
      };

    case 'TOGGLE_HISTORY':
      return { ...state, showHistory: !state.showHistory };

    default:
      return state;
  }
}
